import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  Image,
  Link,
  Youtube,
  Table,
  Undo,
  Redo,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

interface EditorToolbarProps {
  editor: Editor;
  onImageClick: () => void;
  onYoutubeClick: () => void;
  onLinkClick: () => void;
  onTableClick: () => void;
}

export function EditorToolbar({
  editor,
  onImageClick,
  onYoutubeClick,
  onLinkClick,
  onTableClick,
}: EditorToolbarProps) {
  const headingLevel = editor.isActive("heading", { level: 1 })
    ? "1"
    : editor.isActive("heading", { level: 2 })
      ? "2"
      : editor.isActive("heading", { level: 3 })
        ? "3"
        : "p";

  const handleHeadingChange = (value: string) => {
    if (value === "p") {
      editor.chain().focus().setParagraph().run();
    } else {
      editor
        .chain()
        .focus()
        .toggleHeading({ level: parseInt(value) as 1 | 2 | 3 })
        .run();
    }
  };

  const chipCls =
    "h-8 w-8 p-0 rounded-[8px] bg-muted border border-border text-foreground hover:bg-accent hover:text-primary";
  const activeCls = "bg-accent text-primary border-primary";

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-card p-2.5">
      {/* Undo/Redo */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Desfazer"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Refazer"
      >
        <Redo className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Heading Selector */}
      <Select value={headingLevel} onValueChange={handleHeadingChange}>
        <SelectTrigger
          aria-label="Formato do texto"
          className="h-8 w-[130px] rounded-[8px] bg-muted text-xs"
        >
          <SelectValue placeholder="Formato" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="p">Parágrafo</SelectItem>
          <SelectItem value="1">Título 1</SelectItem>
          <SelectItem value="2">Título 2</SelectItem>
          <SelectItem value="3">Título 3</SelectItem>
        </SelectContent>
      </Select>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Text Formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`${chipCls} ${editor.isActive("bold") ? activeCls : ""}`}
        title="Negrito (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`${chipCls} ${editor.isActive("italic") ? activeCls : ""}`}
        title="Itálico (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`${chipCls} ${editor.isActive("underline") ? activeCls : ""}`}
        title="Sublinhado (Ctrl+U)"
      >
        <Underline className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`${chipCls} ${editor.isActive("strike") ? activeCls : ""}`}
        title="Tachado"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`${chipCls} ${editor.isActive("bulletList") ? activeCls : ""}`}
        title="Lista não ordenada"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`${chipCls} ${editor.isActive("orderedList") ? activeCls : ""}`}
        title="Lista ordenada"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Blocks */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`${chipCls} ${editor.isActive("blockquote") ? activeCls : ""}`}
        title="Citação"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`${chipCls} ${editor.isActive("codeBlock") ? activeCls : ""}`}
        title="Bloco de código"
      >
        <Code className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Separador"
      >
        <Minus className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Media */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onLinkClick}
        className={`${chipCls} ${editor.isActive("link") ? activeCls : ""}`}
        title="Link"
      >
        <Link className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={onImageClick}
        title="Inserir imagem"
      >
        <Image className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={onYoutubeClick}
        title="Inserir vídeo do YouTube"
      >
        <Youtube className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={chipCls}
        onClick={onTableClick}
        title="Inserir tabela"
      >
        <Table className="h-4 w-4" />
      </Button>
    </div>
  );
}
